import React, { useEffect, useRef, useState } from "react";
import { SwipeableDrawer } from "@mui/material";
import { getFetch, postFetch } from "../../../../../../fetch/fetch";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ChatForOpinion from "../../../chatforOpinion";
import styles from "./callhistory.module.css";
import ContactsTable from "./contactstable";
import CallDetail from "./calldetail";
import CallbackDoneModal from "./callbackdonemodal";
import TranscriptModal from "./transcriptmodal";
import ProfileModal from "./profilemodal";
import { compareContacts, matchesFilter, matchesSearch, formatPhone } from "./callhistoryformat";

const toastOptions : any = {
    position: "bottom-right",
    autoClose: 3000,
    hideProgressBar: true,
    theme: "colored"
};

const PAGE_SIZE = 100;
const POLL_MS = 60000;
const API = "/dashboard/callcenter/history";

//통화 내역 페이지 (상담 예약 관리 > 통화 내역) — 좌측 번호별 목록(콜백 큐) + 우측 통화 이력
const CallHistory : React.FC<any> = (props) => {

    const [contacts, setContacts] = useState<any[]>([]);
    const [callbackCount, setCallbackCount] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const [filter, setFilter] = useState<string>("all");
    const [searchInput, setSearchInput] = useState<string>("");
    const [search, setSearch] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
    const [contactDetail, setContactDetail] = useState<any>(null);
    const [calls, setCalls] = useState<any[]>([]);
    const [callsLoading, setCallsLoading] = useState<boolean>(false);
    const [journeys, setJourneys] = useState<{ [callId : string] : { loading : boolean; error : boolean; events : any[] } }>({});
    const [now, setNow] = useState<number>(Date.now());

    const [callbackDoneOpen, setCallbackDoneOpen] = useState<boolean>(false);
    const [transcriptCall, setTranscriptCall] = useState<any>(null);
    const [profileOpen, setProfileOpen] = useState<boolean>(false);
    const [commentContact, setCommentContact] = useState<any>(null);
    const [commentOpen, setCommentOpen] = useState<boolean>(false);

    //폴링/소켓 클로저에서 최신 값을 보도록 ref 미러
    const filterRef = useRef<string>("all");
    const searchRef = useRef<string>("");
    const pageRef = useRef<number>(1);
    const selectedRef = useRef<string | null>(null);
    const callsReloadTimer = useRef<any>(null);

    useEffect(() => { filterRef.current = filter; }, [filter]);
    useEffect(() => { searchRef.current = search; }, [search]);
    useEffect(() => { pageRef.current = page; }, [page]);
    useEffect(() => { selectedRef.current = selectedPhone; }, [selectedPhone]);

    //통화중 경과·콜백 경과 카운트업 1초 틱
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    //검색 디바운스 300ms
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    //필터/검색 변경 → 1페이지부터
    useEffect(() => {
        setPage(1);
        pageRef.current = 1;
        loadContacts(1, true);
    }, [filter, search]);

    //60초 폴링 (1페이지만 다시 읽어 교체 — 스피너 없이, 선택 유지)
    useEffect(() => {
        const timer = setInterval(() => {
            if (pageRef.current === 1) {
                loadContacts(1, true, true);
            }
            if (selectedRef.current) {
                loadCalls(selectedRef.current, true);
            }
        }, POLL_MS);
        return () => clearInterval(timer);
    }, []);

    //소켓: callcenter room 입장, contacts_update 수신 (diff 반영), 재접속 시 재입장
    useEffect(() => {
        const socket = props.socket;
        if (!socket) return;

        const join = () => {
            socket.emit("joinCallCenterRoom");
        };
        const onContactsUpdate = (data : any) => {
            if (!data || !Array.isArray(data.contacts)) return;
            if (typeof data.callbackCount === "number") {
                setCallbackCount(data.callbackCount);
            }
            mergeContacts(data.contacts);
            const sel = selectedRef.current;
            if (sel && data.contacts.some((c : any) => c.phoneNumber === sel)) {
                scheduleCallsReload(sel);
            }
        };

        join();
        socket.on("contacts_update", onContactsUpdate);
        socket.on("connect", join);

        return () => {
            socket.off("contacts_update", onContactsUpdate);
            socket.off("connect", join);
            socket.emit("leaveCallCenterRoom");
        };
    }, [props.socket]);

    const loadContacts = async (targetPage : number, reset : boolean, silent : boolean = false) => {
        if (!silent && reset) {
            setLoading(true);
        }
        try {
            const params = `filter=${encodeURIComponent(filterRef.current)}&search=${encodeURIComponent(searchRef.current)}&page=${targetPage}`;
            const result : any = await getFetch(`${API}/contacts?${params}`);
            if (result.message !== "success") {
                if (!silent) {
                    toast.error(result.message === "CALLCENTER_DISABLED" ? "콜센터 모듈이 서버에서 비활성화되어 있습니다" : "통화 내역을 불러오지 못했습니다", toastOptions);
                }
                setLoading(false);
                return;
            }
            //응답이 도착한 시점의 필터/검색과 다르면(그 사이 바뀜) 버림
            if (result.page !== targetPage && !reset) {
                return;
            }
            setCallbackCount(result.callbackCount || 0);
            setTotal(result.total || 0);
            setContacts((prev) => {
                if (reset) {
                    //선택된 행이 1페이지 밖으로 밀려도 목록에는 남겨 둔다 (우측 패널 유지)
                    const sel = selectedRef.current;
                    const list : any[] = result.contacts || [];
                    if (sel && !list.some((c) => c.phoneNumber === sel)) {
                        const keep = prev.find((c) => c.phoneNumber === sel);
                        if (keep) {
                            return [...list, keep];
                        }
                    }
                    return list;
                }
                const seen = new Set(prev.map((c) => c.phoneNumber));
                return [...prev, ...(result.contacts || []).filter((c : any) => !seen.has(c.phoneNumber))];
            });
        } catch (e) {
            console.log(e);
            if (!silent) {
                toast.error("통화 내역을 불러오지 못했습니다", toastOptions);
            }
        }
        setLoading(false);
    };

    //소켓 diff: 번호 기준 교체/삽입 → 현재 필터·검색으로 거르기 → 서버와 같은 정렬 → 페이지 크기 절단
    const mergeContacts = (incoming : any[]) => {
        setContacts((prev) => {
            const map = new Map<string, any>();
            prev.forEach((c) => map.set(c.phoneNumber, c));
            incoming.forEach((c) => {
                if (matchesFilter(c, filterRef.current) && matchesSearch(c, searchRef.current)) {
                    map.set(c.phoneNumber, c);
                } else if (c.phoneNumber !== selectedRef.current) {
                    map.delete(c.phoneNumber);
                } else {
                    map.set(c.phoneNumber, c);
                }
            });
            const list = Array.from(map.values()).sort(compareContacts);
            const limit = pageRef.current * PAGE_SIZE;
            if (list.length > limit) {
                const sel = selectedRef.current;
                const cut = list.slice(0, limit);
                if (sel && !cut.some((c) => c.phoneNumber === sel)) {
                    const keep = list.find((c) => c.phoneNumber === sel);
                    if (keep) cut.push(keep);
                }
                return cut;
            }
            return list;
        });
        if (selectedRef.current) {
            const mine = incoming.find((c) => c.phoneNumber === selectedRef.current);
            if (mine) {
                setContactDetail(mine);
            }
        }
    };

    const scheduleCallsReload = (phone : string) => {
        if (callsReloadTimer.current) {
            clearTimeout(callsReloadTimer.current);
        }
        callsReloadTimer.current = setTimeout(() => {
            callsReloadTimer.current = null;
            loadCalls(phone, true);
        }, 500);
    };

    const loadCalls = async (phone : string, silent : boolean = false) => {
        if (!silent) {
            setCallsLoading(true);
        }
        try {
            const result : any = await getFetch(`${API}/contacts/calls?phone=${encodeURIComponent(phone)}`);
            if (selectedRef.current !== phone) {
                return;   //그 사이 다른 고객 선택
            }
            if (result.message === "success") {
                setCalls(result.calls || []);
                if (result.contact) {
                    setContactDetail(result.contact);
                }
            } else if (!silent) {
                toast.error("통화 이력을 불러오지 못했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            if (!silent) {
                toast.error("통화 이력을 불러오지 못했습니다", toastOptions);
            }
        }
        setCallsLoading(false);
    };

    const selectPhone = (phone : string) => {
        if (phone === selectedPhone) return;
        setSelectedPhone(phone);
        selectedRef.current = phone;
        setContactDetail(contacts.find((c) => c.phoneNumber === phone) || null);
        setCalls([]);
        setJourneys({});
        loadCalls(phone);
    };

    //여정: 최초 펼침에 조회, 종결 통화는 캐시. 통화중/대기중이면 매번 다시
    const loadJourney = async (call : any) => {
        const cached = journeys[call.callId];
        const live = call.result === "in_progress" || call.result === "waiting";
        if (cached && !cached.error && !live) return;
        setJourneys((prev) => ({ ...prev, [call.callId]: { loading: true, error: false, events: cached ? cached.events : [] } }));
        try {
            const result : any = await getFetch(`${API}/calls/journey?callId=${encodeURIComponent(call.callId)}`);
            if (result.message === "success") {
                setJourneys((prev) => ({ ...prev, [call.callId]: { loading: false, error: false, events: result.events || [] } }));
            } else {
                setJourneys((prev) => ({ ...prev, [call.callId]: { loading: false, error: true, events: [] } }));
            }
        } catch (e) {
            console.log(e);
            setJourneys((prev) => ({ ...prev, [call.callId]: { loading: false, error: true, events: [] } }));
        }
    };

    //숨김/해제: 낙관 갱신 → 실패 시 롤백
    const toggleHidden = async (c : any) => {
        const nextHidden = c.hidden ? 0 : 1;
        if (nextHidden && !window.confirm(`${c.displayName || formatPhone(c.phoneNumber)} 을(를) 숨깁니다. 전체 목록 맨 아래로 내려가고 콜백 필요에서 제외됩니다.`)) {
            return;
        }
        const before = c;
        const optimistic = { ...c, hidden: nextHidden };
        mergeContacts([optimistic]);
        try {
            const result : any = await postFetch(`${API}/contacts/hidden`, { phone: c.phoneNumber, hidden: !!nextHidden });
            if (result.message === "success") {
                if (result.contact) mergeContacts([result.contact]);
                toast.success(nextHidden ? "숨김 처리했습니다" : "숨김을 해제했습니다", toastOptions);
            } else {
                mergeContacts([before]);
                toast.error("처리에 실패했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            mergeContacts([before]);
            toast.error("처리에 실패했습니다", toastOptions);
        }
    };

    const onCallbackDone = (updated : any) => {
        if (updated) {
            mergeContacts([updated]);
            if (selectedRef.current === updated.phoneNumber) {
                loadCalls(updated.phoneNumber, true);
            }
        }
    };

    const openComment = (c : any) => {
        setCommentContact(c);
        setCommentOpen(true);
    };

    const selectedContact = contactDetail && contactDetail.phoneNumber === selectedPhone
        ? contactDetail
        : contacts.find((c) => c.phoneNumber === selectedPhone) || null;

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div className={styles.pageTitle}>통화 내역</div>
                <input
                    className={styles.searchInput}
                    placeholder="번호·이름 검색"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                />
            </div>

            <div className={styles.body}>
                <ContactsTable
                    contacts={contacts}
                    callbackCount={callbackCount}
                    total={total}
                    filter={filter}
                    onFilter={(f : string) => setFilter(f)}
                    selectedPhone={selectedPhone}
                    onSelect={selectPhone}
                    onComment={openComment}
                    onMore={() => { const next = page + 1; setPage(next); pageRef.current = next; loadContacts(next, false); }}
                    loading={loading}
                    now={now}
                />
                <CallDetail
                    selectedPhone={selectedPhone}
                    contact={selectedContact}
                    calls={calls}
                    callsLoading={callsLoading}
                    journeys={journeys}
                    now={now}
                    onLoadJourney={loadJourney}
                    onTranscript={(call : any) => setTranscriptCall(call)}
                    onToggleHidden={toggleHidden}
                    onCallbackDone={() => setCallbackDoneOpen(true)}
                    onProfile={() => setProfileOpen(true)}
                />
            </div>

            <CallbackDoneModal
                open={callbackDoneOpen}
                contact={selectedContact}
                onClose={() => setCallbackDoneOpen(false)}
                onDone={onCallbackDone}
            />
            <TranscriptModal
                open={!!transcriptCall}
                call={transcriptCall}
                onClose={() => setTranscriptCall(null)}
            />
            <ProfileModal
                open={profileOpen}
                phone={selectedPhone}
                onClose={() => setProfileOpen(false)}
            />

            {/* 댓글 SwipeableDrawer (방문상담 댓글과 동일 컴포넌트, chatRoomId = callContacts.id) */}
            <SwipeableDrawer
                anchor="right"
                open={commentOpen}
                onClose={() => { setCommentOpen(false); setCommentContact(null); }}
                onOpen={() => setCommentOpen(true)}
            >
                <div style={{ width: "550px", height: "100%" }}>
                    {commentContact !== null && (
                        <ChatForOpinion
                            handleUpdate2={() => {}}
                            handleUpdate={() => {
                                const target = commentContact;
                                mergeContacts([{ ...target, commentCount: (target.commentCount || 0) + 1 }]);
                                setCommentContact((prev : any) => prev ? { ...prev, commentCount: (prev.commentCount || 0) + 1 } : prev);
                            }}
                            openControl={commentOpen}
                            user={props.user}
                            studentName={commentContact.displayName || formatPhone(commentContact.phoneNumber)}
                            chatRoomId={commentContact.id}
                            unreadMessage={() => {}}
                            messageApiEndpoint="https://peetsunbae.com/dashboard/callcenter/history/contacts/comments"
                        />
                    )}
                </div>
            </SwipeableDrawer>
        </div>
    );
};

export default CallHistory;
